import { useHasFocus } from '@axo/shared/ui/input/hooks/useHasFocus';
import {
  FormatterFactory,
  IFormattedInput,
} from '@axo/shared/ui/input/lib/FormattedInput';
import TextInputUI from '@axo/shared/ui/input/lib/TextInput/TextInputUI';
import { FocusEvent, ReactNode, useEffect } from 'react';
import { FieldError, useFormContext } from 'react-hook-form';
import { usePrimaryState, useReloadValue, useValidationUI } from '../../hooks';
import { IField } from '../UI.types';

export interface TextFieldUIProps<Fields>
  extends IField<Fields>,
    Omit<IFormattedInput, 'name'> {
  label?: string;
  format?: string | FormatterFactory;
  suffix?: string;
  defaultFromField?: string;
  placeholder?: string;
  autoSetValues?: boolean;
  disableSuccessState?: boolean;
  error?: FieldError;
  shouldTouch?: boolean;
  valid?: boolean;
  confirmationPrompt?: ReactNode;
}

export function TextFieldUI<Fields>({
  name,
  warningValidation,
  tooltipValidation,
  successValidation,
  registerOptions,
  defaultFromField,
  autoSetValues,
  disableSuccessState,
  error,
  shouldTouch,
  valid,
  ...props
}: TextFieldUIProps<Fields>) {
  const { register, setValue, clearErrors, getValues } = useFormContext();

  const max = registerOptions?.max;
  const min = registerOptions?.min;
  const value = getValues(name);

  const registerProps = register(name, {
    onBlur: setValues,
    ...registerOptions,
  });
  const { hasFocus, ...useHasFocusEventHandlers } = useHasFocus(registerProps);

  const { isValidating, infoBox } = useValidationUI({
    name,
    hasFocus,
    warningValidation,
    tooltipValidation,
    successValidation,
    formControllerError: error,
  });

  const primaryState = usePrimaryState({
    isValidating,
    infoBoxType: infoBox?.type,
    name,
    hasFocus,
    disableSuccessState,
    valid,
  });

  const linkedValue = defaultFromField ? getValues(defaultFromField) : '';
  useEffect(() => {
    if (linkedValue) {
      setValue(name, linkedValue, { shouldDirty: true });
    }
  }, [name, setValue, linkedValue]);

  useReloadValue(name, { shouldTouch }); // This must be after the linkedValue useEffect, need to come up with a better solution

  function setValues(e: FocusEvent<HTMLInputElement>) {
    props.onBlur?.(e);
    if (!autoSetValues) return;

    if (max && Number(e.target.value) > Number(max)) {
      clearErrors(name);
      return setValue(name, max as typeof value);
    }

    if (min && Number(e.target.value) < Number(min)) {
      clearErrors(name);
      return setValue(name, min as typeof value);
    }

    return setValue(name, e.target.value as typeof value);
  }

  return (
    <TextInputUI
      {...props}
      {...registerProps}
      {...useHasFocusEventHandlers}
      infoBox={infoBox}
      value={value}
      state={primaryState}
    />
  );
}
